import { difference, set, cloneDeep } from 'lodash-es';
import { createLogger } from './logger';

export const createStore = (initialState = {}, logPrefix) => {
  let state = initialState;
  let callbacks = [];

  const { log } = createLogger(`[STORE] ${logPrefix} state update`);

  const setState = newState => {
    log({
      prevState: state,
      paylod: newState,
      nextState: {
        ...state,
        ...newState,
      },
    });
    state = {
      ...state,
      ...newState,
    };
    callbacks.forEach(val => val(state));
  };

  const resetState = (newState = {}) => {
    state = { ...newState };
    callbacks.forEach(val => val(state));
  };

  const getState = () => state;

  const subscribe = fn => {
    callbacks.push(fn);

    return () => {
      callbacks = callbacks.filter(val => val !== fn);
    };
  };

  return {
    setState,
    getState,
    resetState,
    subscribe,
  };
};

const inMemoryStorage = {};

/* If localStorage fails, fallback to inMemory */
const storage = {
  getItem: key => {
    try {
      return localStorage.getItem(key);
    } catch {
      return inMemoryStorage[key] || null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      inMemoryStorage[key] = value; // keep in sync
    } catch {
      inMemoryStorage[key] = value;
    }
  },
};

const undefinedIfEmpty = array => (array.length === 0 ? undefined : array);
const shallowKeys = keys =>
  undefinedIfEmpty((keys || []).filter(key => !key.includes('.')));
const deepKeys = keys =>
  undefinedIfEmpty((keys || []).filter(key => key.includes('.')));

export const persistor = ({
  whitelist,
  blacklist,
  key,
  localStorage = storage,
} = {}) => store => {
  if (typeof whitelist === 'string' || typeof blacklist === 'string') {
    throw new Error('Whitelist/blacklist should be an array, not a string');
  }
  const persistState = _state => {
    // to avoid deeply mutating provided state in the case of blacklist deep key,
    // always clone the entire state before running any persistor logic.
    const state = cloneDeep(_state);
    const persistKeys = whitelist
      ? shallowKeys(whitelist)
      : blacklist
      ? difference(Object.keys(state), shallowKeys(blacklist))
      : Object.keys(state);

    const persisted = persistKeys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: state[key],
      }),
      {}
    );

    if (deepKeys(whitelist)) {
      throw new Error('Deep key whitelist persistance not supported');
    }

    if (deepKeys(blacklist)) {
      // there is no "deep" way to omit, so just deep-set to blank
      deepKeys(blacklist).forEach(key => set(persisted, key, ''));
    }

    localStorage.setItem(persistKey, JSON.stringify(persisted));
  };

  const persistKey = `persistor-${key}`;
  const persistedState = JSON.parse(localStorage.getItem(persistKey));
  const currentState = store.getState();
  const hydratedState = { ...currentState, ...persistedState };
  store.setState(hydratedState);
  persistState(hydratedState);
  const unsub = store.subscribe(state => {
    persistState(state);
  });
  return () => unsub();
};
